import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { F90K } from '../../services/p9207Api';
import { useP9207Mode } from './hooks/useP9207Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useP9207Record } from './hooks/useP9207Record';
import { useP9207Navigation } from './hooks/useP9207Navigation';
import { useP9207Permissions } from './hooks/useP9207Permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Approval from './components/Tab2Approval';
import ActionBar from './components/ActionBar';
import { getApprovalStatus, isApprovalLocked, isFieldDisabledFn } from './helpers';
import './P9207Form.css';

const P9207Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useP9207Permissions();
  const { mode, setMode, isEditing } = useP9207Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError,
  } = useP9207Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useP9207Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load P9207 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: F90K) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.f90k_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      // BR-F02: cannot modify if any approval has been given
      if (isApprovalLocked(record)) {
        message.error('已審核的申請單不可修改');
        return;
      }
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    // BR-F02: cannot delete if any approval has been given
    if (isApprovalLocked(record)) {
      message.error('已審核的申請單不可刪除');
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除離職申請單 ${record.f90k_01}?`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能開發中');
  }, [record, isEditing]);

  // Computed Values
  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing, mode), [isEditing, mode]);
  const statusBadge = getApprovalStatus(record);

  // Render
  return (
    <PermissionGuard module={Modules.P9207} action="query">
    <div className="p9207-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>人事管理</a>
              <span>/</span>
              <span className="current">離職申請單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>離職申請單</h1>
            <span className="badge badge-default">9207</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <div className="field-group">
            <label>單號</label>
            <input
              value={String(formData.f90k_01 || '')}
              disabled
              readOnly
              style={{ width: 120 }}
            />
          </div>
          <div className="field-group">
            <label>員工</label>
            <span style={{ fontSize: 13 }}>
              {String(formData.f90k_11 || '')} {names.employee ? `(${names.employee})` : ''}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['離職申請資料', '核准'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 離職申請資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              names={names}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              getFieldError={getFieldError}
            />
          </div>

          {/* Tab 2: 核准 (all readonly) */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Approval
              formData={formData}
              names={names}
            />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default P9207Form;
